import React, { useState, useEffect, useCallback } from 'react'
import { Ticket, Plus, Filter, Trash2, Ban, CheckSquare, Square, Download, Copy, Search } from 'lucide-react'
import { useApi } from '../../hooks/useApi'
import StatusBadge from '../../components/StatusBadge'
import Pagination from '../../components/admin/Pagination'
import CodeGenerateModal from '../../components/admin/CodeGenerateModal'

const ConfirmationModal = ({ isOpen, onClose, onConfirm, action, count }) => {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="card max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold mb-3 text-text-primary">
          Confirm {action === 'revoke' ? 'Revoke' : 'Delete'}
        </h3>
        <p className="text-text-secondary mb-4">
          Are you sure you want to {action} {count} selected code{count !== 1 ? 's' : ''}? 
          This action cannot be undone.
        </p>
        <div className="flex items-center justify-end space-x-3">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button 
            onClick={onConfirm} 
            className={`btn ${action === 'revoke' ? 'btn-error' : 'btn-error'}`}
          >
            {action === 'revoke' ? 'Revoke Codes' : 'Delete Codes'}
          </button>
        </div>
      </div>
    </div>
  )
}

const Codes = () => {
  const [codes, setCodes] = useState([])
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [showGenerate, setShowGenerate] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [selectedCodes, setSelectedCodes] = useState(new Set())
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [pendingAction, setPendingAction] = useState(null)
  const [filters, setFilters] = useState({
    product_id: '',
    status: '',
    batch_id: '',
    search: '',
    page: 1,
    limit: 20,
  })
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [searchInput, setSearchInput] = useState('')
  const api = useApi()

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(f => ({ ...f, search: searchInput, page: 1 }))
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  const fetchCodes = async () => {
    setLoading(true)
    try {
      const params = {}
      if (filters.product_id) params.product_id = filters.product_id
      if (filters.status) params.status = filters.status
      if (filters.batch_id) params.batch_id = filters.batch_id
      if (filters.search) params.search = filters.search
      params.page = filters.page
      params.limit = filters.limit

      const data = await api.getCodes(params)
      setCodes(data.codes || [])
      setTotalPages(data.total_pages || 1)
      setTotalCount(data.total || 0)
      // Clear selections when data changes
      setSelectedCodes(new Set())
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const fetchProducts = async () => {
    try {
      const data = await api.getAdminProducts()
      setProducts(data.products || [])
    } catch (err) {
      console.error(err)
    }
  }

  useEffect(() => { fetchProducts() }, [])
  useEffect(() => { fetchCodes() }, [filters])

  const handleGenerate = async (productId, count, expiresAt, notes) => {
    setGenerating(true)
    try {
      const result = await api.generateCodes(productId, count, expiresAt, notes)
      await fetchCodes()
      return result
    } catch (err) {
      alert(err.message)
      return null
    } finally {
      setGenerating(false)
    }
  }

  const handleRevoke = async (code) => {
    if (!confirm(`Revoke code ${formatCode(code.code)}?`)) return
    try {
      await api.revokeCode(code.id)
      await fetchCodes()
    } catch (err) {
      alert(err.message)
    }
  }

  // Selection handlers
  const isAllSelected = codes.length > 0 && selectedCodes.size === codes.length
  const isSomeSelected = selectedCodes.size > 0 && selectedCodes.size < codes.length

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedCodes(new Set())
    } else {
      setSelectedCodes(new Set(codes.map(c => c.id)))
    }
  }

  const handleSelectCode = (codeId) => {
    const newSelected = new Set(selectedCodes)
    if (newSelected.has(codeId)) {
      newSelected.delete(codeId)
    } else {
      newSelected.add(codeId)
    }
    setSelectedCodes(newSelected)
  }

  // Mass actions
  const handleMassAction = (action) => {
    setPendingAction(action)
    setShowConfirmModal(true)
  }

  const confirmMassAction = async () => {
    if (!pendingAction) return

    try {
      const selectedIds = Array.from(selectedCodes)
      
      if (pendingAction === 'revoke') {
        for (const codeId of selectedIds) {
          await api.revokeCode(codeId)
        }
      } else if (pendingAction === 'delete') {
        // Assuming there's a bulk delete API
        for (const codeId of selectedIds) {
          await api.deleteCode(codeId) // This API might not exist
        }
      }

      await fetchCodes()
      setSelectedCodes(new Set())
    } catch (err) {
      alert(err.message)
    } finally {
      setShowConfirmModal(false)
      setPendingAction(null)
    }
  }

  const exportSelected = () => {
    const selectedCodeData = codes.filter(c => selectedCodes.has(c.id))
    const csvContent = "data:text/csv;charset=utf-8,"
      + "Code,Product,Status,Batch,Created,Expires\n"
      + selectedCodeData.map(c => 
          `"${c.code}","${c.product_name}","${c.status}","${c.batch_id || ''}","${c.created_at}","${c.expires_at || ''}"`
        ).join("\n")

    const encodedUri = encodeURI(csvContent)
    const link = document.createElement("a")
    link.setAttribute("href", encodedUri)
    link.setAttribute("download", `codes_export_${new Date().toISOString().split('T')[0]}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const copySelected = () => {
    const selectedCodeData = codes.filter(c => selectedCodes.has(c.id))
    const codeList = selectedCodeData.map(c => c.code).join('\n')
    navigator.clipboard.writeText(codeList).then(() => {
      alert(`Copied ${selectedCodeData.length} codes to clipboard`)
    })
  }

  const formatCode = (code) => {
    if (!code || code.length < 15) return code
    return `${code.slice(0, 3)}-${code.slice(3, 7)}-${code.slice(7, 11)}-${code.slice(11, 15)}`
  }

  const formatDate = (d) => {
    if (!d) return '—'
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Ticket size={24} className="text-accent" />
          <h1 className="text-2xl font-bold text-text-primary">Codes</h1>
        </div>
        <button onClick={() => setShowGenerate(true)} className="btn btn-primary">
          <Plus size={16} className="mr-2" />
          Generate Codes
        </button>
      </div>

      {/* Mass Action Toolbar */}
      {selectedCodes.size > 0 && (
        <div className="card bg-accent/5 border-accent/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-text-secondary">
                {selectedCodes.size} code{selectedCodes.size !== 1 ? 's' : ''} selected
              </span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleMassAction('revoke')}
                  className="btn btn-error text-sm px-3 py-1.5"
                >
                  <Ban size={14} className="mr-1" />
                  Revoke Selected
                </button>
                <button
                  onClick={() => handleMassAction('delete')}
                  className="btn btn-error text-sm px-3 py-1.5"
                >
                  <Trash2 size={14} className="mr-1" />
                  Delete Selected
                </button>
                <button
                  onClick={exportSelected}
                  className="btn btn-secondary text-sm px-3 py-1.5"
                >
                  <Download size={14} className="mr-1" />
                  Export
                </button>
                <button
                  onClick={copySelected}
                  className="btn btn-secondary text-sm px-3 py-1.5"
                >
                  <Copy size={14} className="mr-1" />
                  Copy Codes
                </button>
              </div>
            </div>
            <button
              onClick={() => setSelectedCodes(new Set())}
              className="text-text-muted hover:text-text-secondary"
            >
              Clear selection
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="flex items-center space-x-2 mb-4">
          <Filter size={16} className="text-text-muted" />
          <span className="text-sm font-medium text-text-secondary">Filters</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <select
            value={filters.product_id}
            onChange={(e) => setFilters(f => ({ ...f, product_id: e.target.value, page: 1 }))}
            className="input"
          >
            <option value="">All Products</option>
            {products.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          <select
            value={filters.status}
            onChange={(e) => setFilters(f => ({ ...f, status: e.target.value, page: 1 }))}
            className="input"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="redeemed">Redeemed</option>
            <option value="expired">Expired</option>
            <option value="revoked">Revoked</option>
          </select>

          <input
            type="text"
            value={filters.batch_id}
            onChange={(e) => setFilters(f => ({ ...f, batch_id: e.target.value, page: 1 }))}
            placeholder="Batch ID..."
            className="input"
          />

          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search code..."
              className="input pl-10 font-mono"
            />
          </div>
        </div>
      </div>

      {/* Codes table */}
      <div className="card p-0 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="spinner w-8 h-8"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th className="w-12">
                      <button
                        onClick={handleSelectAll}
                        className="text-text-muted hover:text-text-primary"
                        title={isAllSelected ? 'Deselect all' : 'Select all'}
                      >
                        {isAllSelected ? (
                          <CheckSquare size={16} />
                        ) : isSomeSelected ? (
                          <CheckSquare size={16} className="text-accent" />
                        ) : (
                          <Square size={16} />
                        )}
                      </button>
                    </th>
                    <th>Code</th>
                    <th>Product</th>
                    <th>Status</th>
                    <th>Batch</th>
                    <th>Created</th>
                    <th>Expires</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {codes.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-8 text-text-muted">
                        No codes found
                      </td>
                    </tr>
                  ) : (
                    codes.map((c) => (
                      <tr key={c.id} className={selectedCodes.has(c.id) ? 'bg-accent/5' : ''}>
                        <td>
                          <button
                            onClick={() => handleSelectCode(c.id)}
                            className="text-text-muted hover:text-text-primary"
                          >
                            {selectedCodes.has(c.id) ? (
                              <CheckSquare size={16} className="text-accent" />
                            ) : (
                              <Square size={16} />
                            )}
                          </button>
                        </td>
                        <td>
                          <button
                            onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(c.code); }}
                            className="font-mono text-accent text-xs hover:text-accent-dark flex items-center space-x-1 group"
                            title="Click to copy"
                          >
                            <span className="break-all">{formatCode(c.code)}</span>
                            <Copy size={11} className="opacity-0 group-hover:opacity-100 flex-shrink-0" />
                          </button>
                        </td>
                        <td className="text-text-primary text-sm">{c.product_name || '—'}</td>
                        <td><StatusBadge status={c.status} size="xs" /></td>
                        <td className="text-text-muted text-xs font-mono">{c.batch_id || '—'}</td>
                        <td className="text-text-muted text-xs">{formatDate(c.created_at)}</td>
                        <td className="text-text-muted text-xs">{formatDate(c.expires_at)}</td>
                        <td>
                          {c.status === 'active' && (
                            <button
                              onClick={() => handleRevoke(c)}
                              className="text-error hover:text-error/80 transition-colors p-1"
                              title="Revoke code"
                            >
                              <Ban size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              page={filters.page}
              totalPages={totalPages}
              total={totalCount}
              limit={filters.limit}
              onPageChange={(p) => setFilters(f => ({ ...f, page: p }))}
              onLimitChange={(l) => setFilters(f => ({ ...f, limit: l, page: 1 }))}
            />
          </>
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmMassAction}
        action={pendingAction}
        count={selectedCodes.size}
      />

      {/* Generate Modal */}
      {showGenerate && (
        <CodeGenerateModal
          products={products}
          onGenerate={handleGenerate}
          onClose={() => setShowGenerate(false)}
          loading={generating}
        />
      )}
    </div>
  )
}

export default Codes